// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import {
    getFulfillmentLabel,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import { ellipsis } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentLabel: getFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
    // XNG-Changes
    changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    isAuthenticated: xIsAuthenticated(state),
    isNonPurchasable: getIsNonPurchasable(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    isPickUp: getSession(state).fulfillmentMethod === FulfillmentMethod.PickUp,
});

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // XNG-Changes
    titleLink?: LinkPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleLink: {
        typographyProps: {
            size: 12,
            transform: "capitalize",
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 16px;
                text-align: left;
                ${ellipsis(1)}
            `,
        },
        css: css`
            max-width: 290px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        size: 12,
        transform: "uppercase",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-right: 8px;
            white-space: nowrap;
        `,
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    changeCustomerPageUrl,
    isAuthenticated,
    isNonPurchasable,
    signInUrl,
    isPickUp,
}) => {
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    // XNG-Changes: Update structure
    let url = changeCustomerPageUrl;
    let label = fulfillmentLabel;
    if (!isAuthenticated || !fulfillmentLabel) {
        url = signInUrl;
        label = isPickUp ? translate("Select My Branch") : translate("Select My Shipping Address");
    }

    // XNG-Changes: Update structure
    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                {!isNonPurchasable && (
                    <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                        {/* XNG- check for fulfillment method */}
                        {isPickUp ? translate("Branch") : translate("Shipping Address")}:
                    </Typography>
                )}
                {!isNonPurchasable && (
                    <Link {...styles.titleLink} href={url} data-test-selector="header_shipToButton">
                        {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                        please also make changes to `NavigationDrawer.tsx` when you change this. */}
                        {label}
                    </Link>
                )}
            </StyledWrapper>
            {/* <Drawer */}
            {/*    {...drawerStyles} */}
            {/*    position="top" */}
            {/*    isOpen={isDrawerOpen} */}
            {/*    headline={isPunchOutSession ? "" : translate("Change Customer")} */}
            {/*    handleClose={handleCloseDrawer} */}
            {/*    data-test-selector="addressDrawer" */}
            {/* > */}
            {/*    <Zone zoneName="Container" contentId={id} /> */}
            {/* </Drawer> */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
